import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useGame } from '../../../backend/useGame';
import { useGameList } from '../../../backend/useGameList';
import { useUser } from '../../../backend/useUser';
import Gallery from '../../website/Gallery';
import { Project } from '../../website/ProjectGrid';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../ui/components/LoadingSpinner';
import { defaultConfig } from '../../../utils/config';
import { navigateToWebsite } from '../../../utils/urls';

const GalleryContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    overflow: visible;
`;

interface GameGalleryProps {
    colormode: 'light' | 'dark';
    tabs: Array<{ name: string; icon?: string; disabled?: boolean }>
}

export interface GameMetrics {
    plays: number;
    likes: number;
    remixes: number;
}

interface ProjectWithGameMetrics extends Project {
    gameMetrics: GameMetrics;
    updatedAt?: string;
}

const ITEMS_PER_PAGE = 12;

const GameGallery: React.FC<GameGalleryProps> = ({ colormode, tabs }) => {
    const [projects, setProjects] = useState<ProjectWithGameMetrics[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [activeTab, setActiveTab] = useState('My Games');
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const { user, loading: userLoading } = useUser();
    const game = useGame();
    const { listMyGames, listAllGames } = useGameList();
    const navigate = useNavigate();

    // Set initial tab based on user authentication
    useEffect(() => {
        if (!userLoading) {
            if (!user) {
                setActiveTab('Community');
            }
        }
    }, [user, userLoading]);

    // Function to convert game data to project format
    const gameToProject = useCallback((game: any): ProjectWithGameMetrics => {
        // Format the date if it exists
        const formatDate = (dateString?: string) => {
            if (!dateString) return '';
            
            const date = new Date(dateString);
            // Format as: Apr 20, 2025
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                // year: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
            });
        };
        
        return {
            id: game.id,
            name: game.game_name,
            username: game.user_tag, // TODO: Add real username
            imageUrl: game.screenshot_url || '/assets/game-screenshot.png', // Use default image if no screenshot
            linkType: activeTab === 'My Games' ? 'game' : 'play',
            gameMetrics: game.gameMetrics || {
                plays: 0,
                likes: 0,
                remixes: 0,
            },
            updatedAt: formatDate(game.updated_at)
        };
    }, [activeTab]);

    // Memoize the transformed projects
    const memoizedProjects = useMemo(() => projects, [projects]);

    // Generate placeholder projects for non-My Projects tabs
    const generatePlaceholderProjects = useCallback((pageNum: number): ProjectWithGameMetrics[] => {
        const startIndex = (pageNum - 1) * ITEMS_PER_PAGE;
        return Array.from({ length: ITEMS_PER_PAGE }, (_, i) => ({
            id: `${pageNum}-${i}`,
            name: `${activeTab} Project ${startIndex + i + 1}`,
            username: `@user${startIndex + i + 1}`,
            imageUrl: `https://picsum.photos/400/225?random=${pageNum}-${i}`,
            gameMetrics: {
                plays: Math.floor(Math.random() * 100),
                likes: Math.floor(Math.random() * 50),
                remixes: Math.floor(Math.random() * 10),
            }
        }));
    }, [activeTab]);

    // Function to load projects based on active tab
    const loadProjects = useCallback(async (pageNum: number, currentTab: string) => {
        if (userLoading) return;
        
        setLoading(true);
        
        try {
            if (currentTab === 'My Games' && user) {
                const { games, hasMore: more } = await listMyGames(user.id, pageNum, 12, true); // Include game metrics
                const newProjects = games.map(gameToProject);
                
                // If this is the first load and there are no projects, switch to Community tab
                if (pageNum === 1 && newProjects.length === 0 && !initialLoadDone) {
                    setActiveTab('Community');
                    // We'll load Community projects in the next effect cycle
                    setInitialLoadDone(true);
                    setLoading(false);
                    return;
                }
                
                setProjects(prev => pageNum === 1 ? newProjects : [...prev, ...newProjects]);
                setHasMore(more);
                // console.log('loading my:', newProjects);
            } else {
                const { games, hasMore: more } = await listAllGames(null, pageNum, 12, true); // Include game metrics
                const newProjects = games.map(gameToProject);
                setProjects(prev => pageNum === 1 ? newProjects : [...prev, ...newProjects]);
                setHasMore(more);
                // console.log('loading community:', newProjects);
            }
            
            if (pageNum === 1) {
                setInitialLoadDone(true);
            }
        } catch (error) {
            console.error('Error loading projects:', error);
            setHasMore(false);
            setProjects(pageNum === 1 ? [] : prev => prev);
            
            if (pageNum === 1) {
                setInitialLoadDone(true);
            }
        } finally {
            setLoading(false);
        }
    }, [gameToProject, user, userLoading, game, initialLoadDone]);

    // Load initial projects when tab changes or user loads
    useEffect(() => {
        // Only load if user loading is complete
        if (!userLoading) {
            // console.log('Loading initial projects...');
            // Reset page and loading state when tab changes
            setPage(1);
            
            // Always load the current active tab data
            loadProjects(1, activeTab);
        }
    }, [activeTab, userLoading, user]);

    const handleLoadMore = useCallback(async () => {
        const nextPage = page + 1;
        setPage(nextPage);
        await loadProjects(nextPage, activeTab);
    }, [page, activeTab, loadProjects]);

    const handleProjectClick = useCallback((project: Project) => {
        if (activeTab === 'My Games') {
            // console.log('Clicked my game:', project);
            navigate(`/game/${project.id}`);
        } else {
            // console.log('Community project clicked:', project);
            if (defaultConfig.REDIRECT_TO_WEBSITE) {
                navigateToWebsite(`/play/${project.id}`);
            } else {
                window.open(`/play/${project.id}`, '_blank');
            }
        }
    }, [activeTab, navigate]);

    const handleTabChange = useCallback((tab: string) => {
        setActiveTab(tab);
        setProjects([]); // Clear existing projects when switching tabs
        setPage(1); // Reset to first page
    }, [activeTab]);

    // Filter tabs based on user login state
    const visibleTabs = useMemo(() => {
        if (!user) {
            return []; // tabs.filter(tab => tab.name === 'Community');
        }
        return tabs;
    }, [user, tabs]);

    if (userLoading) {
        return <LoadingSpinner colorMode={colormode}/>;
    }

    return (
        <GalleryContainer>
            <Gallery
                type="Game"
                colormode={colormode}
                projects={memoizedProjects}
                loading={loading}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                onProjectClick={handleProjectClick}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                tabs={visibleTabs}
                userLoading={userLoading}
            />
        </GalleryContainer>
    );
};

export default GameGallery;